import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export interface ChangeEvent {
  id: number;
  at: string;
  type: string;
  entityType?: string;
  entityId?: string;
}

/**
 * Query-key convention used across the app (first key element = resource):
 *   ['shipments', ...] lists, ['shipment', id] detail, ['tracking', ...], ['tasks'...], ['task', id],
 *   ['tickets'...], ['ticket', id], ['payments'...], ['payment', id], ['dashboard', ...], ['notifications'...]
 */
const INVALIDATE: Record<string, string[][]> = {
  // Includes shipping labels (Labels page, label status on Rep Ops label requests).
  'shipment.updated': [['shipments'], ['tracking'], ['dashboard'], ['analytics'], ['labels'], ['rep-ops', 'label']],
  'tracking.event': [['shipments'], ['tracking'], ['dashboard'], ['analytics']],
  'task.updated': [['tasks'], ['dashboard'], ['checklist'], ['rep']],
  // Representative portal: ['rep', ...] (portal screens), ['rep-ops', ...] (staff screens), ['conversations', ...] (chat).
  'rep.updated': [['rep'], ['rep-ops'], ['dashboard']],
  'conversation.message': [['conversations'], ['rep'], ['rep-ops']],
  'ticket.updated': [['tickets'], ['dashboard']],
  // Client services: staff queues (['services', ...]) and the client portal (['portal', ...]).
  'services.updated': [['services'], ['portal'], ['dashboard']],
  'payment.updated': [['payments'], ['dashboard']],
  'payout.updated': [['payouts'], ['payroll'], ['rep'], ['dashboard']],
  'notification.created': [['notifications'], ['rep', 'dashboard'], ['portal'], ['announcements']],
  'announcement.updated': [['announcements']],
  // WhatsApp Inbox: thread list and open conversation.
  'whatsapp.updated': [['whatsapp']],
};
const DETAIL_KEY: Record<string, string> = { shipment: 'shipment', task: 'task', ticket: 'ticket', payment_request: 'payment' };

type Status = 'connecting' | 'live' | 'offline';
const listeners = new Set<(s: Status) => void>();
let currentStatus: Status = 'connecting';
function setStatus(s: Status) {
  currentStatus = s;
  listeners.forEach((l) => l(s));
}

export function useRealtimeStatus() {
  const [s, set] = useState<Status>(currentStatus);
  useEffect(() => {
    listeners.add(set);
    return () => void listeners.delete(set);
  }, []);
  return s;
}

/** Opens the Server-Sent Events stream and refreshes affected data as changes are pushed. */
export function useRealtime(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let es: EventSource | null = null;
    let fallback: ReturnType<typeof setInterval> | null = null;
    let closed = false;

    const connect = () => {
      es = new EventSource('/api/events', { withCredentials: true });
      es.addEventListener('ready', () => {
        setStatus('live');
        if (fallback) clearInterval(fallback);
        fallback = null;
      });
      es.addEventListener('change', (msg) => {
        const ev = JSON.parse((msg as MessageEvent).data) as ChangeEvent;
        for (const key of INVALIDATE[ev.type] ?? []) qc.invalidateQueries({ queryKey: key });
        if (ev.entityType && ev.entityId && DETAIL_KEY[ev.entityType]) qc.invalidateQueries({ queryKey: [DETAIL_KEY[ev.entityType], ev.entityId] });
        window.dispatchEvent(new CustomEvent<ChangeEvent>('ea:change', { detail: ev }));
      });
      es.onerror = () => {
        setStatus('offline');
        // Browser retries automatically; meanwhile poll so dashboards never go stale.
        if (!fallback && !closed) fallback = setInterval(() => qc.invalidateQueries({ queryKey: ['dashboard'] }), 60_000);
      };
    };
    connect();
    return () => {
      closed = true;
      es?.close();
      if (fallback) clearInterval(fallback);
    };
  }, [enabled, qc]);
}
